import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, BarChart3, Clock, CheckCircle, XCircle, 
  FileText, MessageCircle, Download, Search, Filter,
  Calendar, DollarSign, User, Star, ChevronDown,
  Upload, Plus, Eye, Edit, Trash2
} from 'lucide-react';
import axiosInstance from '../utils/axios';
import Modal from '../components/Modal';

const AdminLegalBusinessManagement = ({ type = 'all' }) => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [availableAdmins, setAvailableAdmins] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assigned_to_me: false,
    search: ''
  });
  const [updateData, setUpdateData] = useState({
    status: '',
    priority: '',
    review_notes: '',
    admin_comments: '',
    review_file_url: '',
    review_file_name: ''
  });
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    onConfirm: null,
    showCancel: false
  });

  const closeModal = () => {
    setModal({
      isOpen: false,
      type: 'info',
      title: '',
      message: '',
      onConfirm: null,
      showCancel: false
    });
  };

  useEffect(() => {
    fetchData();
    fetchStats();
    fetchAvailableAdmins();
  }, [type, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.assigned_to_me) params.append('assigned_to_me', 'true');
      if (filters.search) params.append('search', filters.search);
      if (type !== 'all') params.append('type', type);

      const response = await axiosInstance.get(`/admin/legal-business/requests/?${params.toString()}`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to load requests. Please try again.',
        onConfirm: closeModal,
        showCancel: false
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/admin/legal-business/stats/');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAvailableAdmins = async () => {
    try {
      const params = new URLSearchParams();
      if (type !== 'all') params.append('type', type);
      
      const response = await axiosInstance.get(`/admin/legal-business/available-admins/?${params.toString()}`);
      setAvailableAdmins(response.data);
    } catch (error) {
      console.error('Error fetching available admins:', error);
    }
  };

  const handleAssign = async (requestId, adminId = null) => {
    try {
      const response = await axiosInstance.post('/admin/legal-business/assign/', {
        request_id: requestId,
        admin_id: adminId
      });

      setModal({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: response.data.message || 'Request assigned successfully',
        onConfirm: closeModal,
        showCancel: false
      });

      setShowAssignModal(false);
      fetchData();
    } catch (error) {
      console.error('Error assigning request:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error || 'Failed to assign request',
        onConfirm: closeModal,
        showCancel: false
      });
    }
  };

  const handleUpdate = async () => {
    if (!selectedRequest) return;

    try {
      const response = await axiosInstance.post('/admin/legal-business/update/', {
        request_id: selectedRequest.id,
        ...updateData
      });

      setModal({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: response.data.message || 'Request updated successfully',
        onConfirm: closeModal,
        showCancel: false
      });

      setShowUpdateModal(false);
      setUpdateData({
        status: '',
        priority: '',
        review_notes: '',
        admin_comments: '',
        review_file_url: '',
        review_file_name: ''
      });
      fetchData();
      
      if (selectedRequest) {
        fetchRequestDetails(selectedRequest.id);
      }
    } catch (error) {
      console.error('Error updating request:', error);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update request',
        onConfirm: closeModal,
        showCancel: false
      });
    }
  };

  const fetchRequestDetails = async (requestId) => {
    try {
      const response = await axiosInstance.get(`/legal-business/requests/${requestId}/`);
      setSelectedRequest(response.data);
    } catch (error) {
      console.error('Error fetching request details:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-gray-100 text-gray-800', icon: Clock },
      'paid': { color: 'bg-blue-100 text-blue-800', icon: DollarSign },
      'assigned': { color: 'bg-purple-100 text-purple-800', icon: User },
      'in_progress': { color: 'bg-yellow-100 text-yellow-800', icon: Star },
      'completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'cancelled': { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: Clock };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      'low': 'bg-gray-100 text-gray-800',
      'medium': 'bg-blue-100 text-blue-800',
      'high': 'bg-orange-100 text-orange-800',
      'urgent': 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityConfig[priority] || 'bg-gray-100 text-gray-800'}`}>
        {priority.toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRequestTypeIcon = (type) => {
    return type === 'legal' ? (
      <Shield className="w-4 h-4 text-purple-600" />
    ) : (
      <BarChart3 className="w-4 h-4 text-blue-600" />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">
          {type === 'legal' ? 'Legal Document Reviews' : 
           type === 'business' ? 'Business Plan Reviews' : 
           'Legal & Business Support'}
        </h1>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Legal</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_legal}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Business</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_business}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.pending_legal + stats.pending_business}
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  GH₵ {(stats.total_revenue_legal + stats.total_revenue_business).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search requests..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff9702] focus:border-transparent"
              />
            </div>
          </div>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff9702] focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="assigned">Assigned</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>

          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff9702] focus:border-transparent"
          >
            <option value="">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={filters.assigned_to_me}
              onChange={(e) => setFilters({ ...filters, assigned_to_me: e.target.checked })}
              className="w-4 h-4 text-[#ff9702] border-gray-300 rounded focus:ring-[#ff9702]"
            />
            <span className="text-sm text-gray-700">Assigned to me</span>
          </label>

          <button
            onClick={fetchData}
            className="px-4 py-2 bg-[#ff9702] text-white rounded-lg hover:bg-[#e08602] transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Request
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Creator
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assigned To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ff9702]"></div>
                    </div>
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    No requests found
                  </td>
                </tr>
              ) : (
                requests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {getRequestTypeIcon(request.request_type)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {request.title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {request.request_type === 'legal' ? 'Legal Review' : 'Business Review'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{request.creator_name}</div>
                      <div className="text-xs text-gray-500">{request.creator_username}</div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-6 py-4">
                      {getPriorityBadge(request.priority)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {request.assigned_to_name || 'Unassigned'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-medium text-gray-900">
                        GH₵ {request.amount}
                      </span>
                      {request.payment_status === 'successful' && (
                        <span className="ml-2 text-xs text-green-600">Paid</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {formatDate(request.created_at)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {request.days_ago} days ago
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            fetchRequestDetails(request.id);
                            setShowDetailsModal(true);
                          }}
                          className="p-2 text-gray-600 hover:text-[#ff9702] hover:bg-gray-100 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowAssignModal(true);
                          }}
                          className="p-2 text-gray-600 hover:text-[#ff9702] hover:bg-gray-100 rounded-lg transition-colors"
                          title="Assign"
                        >
                          <User className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setUpdateData({
                              status: request.status,
                              priority: request.priority,
                              review_notes: request.review_notes || '',
                              admin_comments: request.admin_comments || ''
                            });
                            setShowUpdateModal(true);
                          }}
                          className="p-2 text-gray-600 hover:text-[#ff9702] hover:bg-gray-100 rounded-lg transition-colors"
                          title="Update"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Request Details</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500">Title</label>
                    <p className="text-sm font-medium text-gray-900">{selectedRequest.title}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Type</label>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedRequest.request_type === 'legal' ? 'Legal Review' : 'Business Review'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Creator</label>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedRequest.creator_details?.full_name || selectedRequest.creator_details?.username}
                    </p>
                    <p className="text-xs text-gray-500">{selectedRequest.creator_details?.email}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Assigned To</label>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedRequest.assigned_to_details?.full_name || 'Unassigned'}
                    </p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-xs text-gray-500">Description</label>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedRequest.description}</p>
                </div>

                {/* Document */}
                <div>
                  <label className="text-xs text-gray-500">Document</label>
                  <div className="mt-2">
                    <a
                      href={selectedRequest.document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      {selectedRequest.document_name}
                    </a>
                  </div>
                </div>

                {/* Review File (if exists) */}
                {selectedRequest.review_file_url && (
                  <div>
                    <label className="text-xs text-gray-500">Review File</label>
                    <div className="mt-2">
                      <a
                        href={selectedRequest.review_file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {selectedRequest.review_file_name || 'Download Review'}
                      </a>
                    </div>
                  </div>
                )}

                {/* Review Notes */}
                {selectedRequest.review_notes && (
                  <div>
                    <label className="text-xs text-gray-500">Review Notes</label>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedRequest.review_notes}</p>
                  </div>
                )}

                {/* Admin Comments */}
                {selectedRequest.admin_comments && (
                  <div>
                    <label className="text-xs text-gray-500">Admin Comments</label>
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedRequest.admin_comments}</p>
                  </div>
                )}

                {/* Payment Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2">Payment Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-500">Amount</label>
                      <p className="text-sm font-medium text-gray-900">GH₵ {selectedRequest.amount}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Payment Status</label>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedRequest.payment_status === 'successful' ? (
                          <span className="text-green-600">Paid</span>
                        ) : (
                          <span className="text-yellow-600">Pending</span>
                        )}
                      </p>
                    </div>
                    {selectedRequest.payment_date && (
                      <div>
                        <label className="text-xs text-gray-500">Payment Date</label>
                        <p className="text-sm text-gray-900">{formatDate(selectedRequest.payment_date)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Messages */}
                {selectedRequest.messages && selectedRequest.messages.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Messages</h3>
                    <div className="space-y-4">
                      {selectedRequest.messages.map((msg) => (
                        <div key={msg.id} className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <span className="font-medium text-gray-900">{msg.sender_name}</span>
                            <span className="text-xs text-gray-500">{formatDate(msg.created_at)}</span>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.message}</p>
                          {msg.attachment_url && (
                            <a
                              href={msg.attachment_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center mt-2 text-xs text-[#ff9702] hover:text-[#e08602]"
                            >
                              <Download className="w-3 h-3 mr-1" />
                              {msg.attachment_name || 'Attachment'}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Assign Request</h2>
              <p className="text-sm text-gray-600 mb-4">
                Assign "{selectedRequest.title}" to an admin
              </p>

              <div className="space-y-4 max-h-96 overflow-y-auto">
                <button
                  onClick={() => handleAssign(selectedRequest.id, null)}
                  className="w-full p-3 text-left bg-[#ff9702] text-white rounded-lg hover:bg-[#e08602] transition-colors"
                >
                  Assign to myself
                </button>

                {availableAdmins
                  .filter(admin => admin.id !== user?.id)
                  .map(admin => (
                    <button
                      key={admin.id}
                      onClick={() => handleAssign(selectedRequest.id, admin.id)}
                      className="w-full p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <div className="font-medium text-gray-900">
                        {admin.full_name || admin.username}
                      </div>
                      <div className="text-xs text-gray-500">{admin.email}</div>
                    </button>
                  ))}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Modal */}
      {showUpdateModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Update Request</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={updateData.status}
                    onChange={(e) => setUpdateData({ ...updateData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff9702] focus:border-transparent"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="assigned">Assigned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={updateData.priority}
                    onChange={(e) => setUpdateData({ ...updateData, priority: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff9702] focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Review Notes
                  </label>
                  <textarea
                    value={updateData.review_notes}
                    onChange={(e) => setUpdateData({ ...updateData, review_notes: e.target.value })}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff9702] focus:border-transparent"
                    placeholder="Add review notes..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Admin Comments (Internal)
                  </label>
                  <textarea
                    value={updateData.admin_comments}
                    onChange={(e) => setUpdateData({ ...updateData, admin_comments: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff9702] focus:border-transparent"
                    placeholder="Add internal comments..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Review File URL
                  </label>
                  <input
                    type="url"
                    value={updateData.review_file_url}
                    onChange={(e) => setUpdateData({ ...updateData, review_file_url: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff9702] focus:border-transparent"
                    placeholder="https://..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Review File Name
                  </label>
                  <input
                    type="text"
                    value={updateData.review_file_name}
                    onChange={(e) => setUpdateData({ ...updateData, review_file_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ff9702] focus:border-transparent"
                    placeholder="review_document.pdf"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowUpdateModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  className="px-4 py-2 bg-[#ff9702] text-white rounded-lg hover:bg-[#e08602] transition-colors"
                >
                  Update Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onConfirm={modal.onConfirm}
        showCancel={modal.showCancel}
        confirmText={modal.confirmText || 'OK'}
        cancelText={modal.cancelText || 'Cancel'}
      />
    </div>
  );
};

export default AdminLegalBusinessManagement;